import * as React from "react";

import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";

import * as forms from "@client/site/forms";
import {Response, server} from "@reactivated";

export const CheckIn = router.CheckIn.withHooks((props) => {
    const checkIn = props.resolved.journal.check_in;

    if (checkIn == null) {
        throw new Error("Whoops");
    }

    const isReadOnly = checkIn.existing != null;

    const [responses, setResponses] = React.useState(
        checkIn.responses.map((response) => ({
            question: response.question.id,
            response: response.response ?? "",
        })),
    );

    const [notes, setNotes] = React.useState(checkIn.existing?.notes ?? "");
    const [errors, setErrors] = React.useState<(string[] | null)[]>(
        checkIn.responses.map(() => null),
    );

    return {
        checkIn,
        isReadOnly,
        responses,
        setResponses,
        notes,
        setNotes,
        errors,
        setErrors,
    };
})
    .withIndex((props) => {
        return (
            <div style={{display: "flex", flexDirection: "column", gap: 25}}>
                {props.checkIn.responses.map((responseData, index) => {
                    const options = responseData.question.response_labels.map(
                        ({value, label}) => [value, label] as const,
                    );

                    const field: forms.PFieldHandler<string> = {
                        type: "select",
                        name: `response_${index}`,
                        label: responseData.question.label,
                        required: true,
                        value: props.responses[index].response,
                        options,
                        readOnly: props.isReadOnly,
                        errors: props.errors[index],
                        onChange: (value: string) => {
                            props.setResponses((prev) =>
                                prev.map((r, i) =>
                                    i === index ? {...r, response: value} : r,
                                ),
                            );
                            props.setErrors((prev) =>
                                prev.map((e, i) => (i === index ? null : e)),
                            );
                        },
                    };

                    return (
                        <div key={index}>
                            <forms.PField field={field} />
                        </div>
                    );
                })}
                <forms.PField
                    field={{
                        type: "textarea",
                        name: "notes",
                        label: props.checkIn.notes_label,
                        value: props.notes,
                        readOnly: props.isReadOnly,
                        errors: null,
                        onChange: (value: string) => {
                            props.setNotes(value);
                        },
                    }}
                />
            </div>
        );
    })
    .withActions((props) => [
        props.checkIn.existing == null
            ? {
                  icon: "save",
                  title: "Save",
                  onClick: async () => {
                      const newErrors = props.responses.map((r) =>
                          r.response === "" ? ["This field is required"] : null,
                      );

                      if (newErrors.some((e) => e != null)) {
                          props.setErrors(newErrors);
                          return;
                      }

                      const result = await server.journal.day.check_in({
                          key: props.resolved.journal.day.key,
                          user_id: props.currentlyManagedUserId,
                          notes: props.notes,
                          responses: props.responses.map((r) => ({
                              question: r.question,
                              response: r.response as Response,
                          })),
                      });

                      if (result.type === "success") {
                          const eventProperties = Object.fromEntries(
                              props.checkIn.responses.map((responseData, index) => {
                                  return [
                                      `check_in_${responseData.question.type}`,
                                      props.responses[index].response,
                                  ] as const;
                              }),
                          );

                          void analytics.track("CHECK_IN_SUBMITTED", eventProperties);
                          props.notify("Check-in submitted");
                          router.push("journal.journal", {
                              key: props.resolved.journal.day.key,
                          });
                      }
                  },
              }
            : null,
    ])
    .withOptions(() => ({
        isDelegatable: true,
        title: `Check In`,
        back: "Journal",
        actionsStyle: "contrast",
    }));
